import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { ArrowLeft, Star, Clock, MapPin, Heart, Plus, Minus, Bike, X, ChevronDown, Info } from "lucide-react";
import { isRestaurantOpen } from "@/lib/restaurantUtils";

export default function RestaurantPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, cart } = useCart();
  const [restaurant, setRestaurant] = useState(null);
  const [category, setCategory] = useState("Popular");
  const [fav, setFav] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [fulfillment, setFulfillment] = useState("delivery"); // delivery | pickup
  const scrollRef = useRef(null);

  const isOpen = restaurant ? isRestaurantOpen(restaurant) : true;

  useEffect(() => {
    api.get(`/restaurants/${id}`).then((r) => setRestaurant(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
    api.get("/favorites").then((r) => setFav(!!r.data.find((x) => x.restaurant_id === id))).catch(() => {});
  }, [id]);

  const menuCategories = useMemo(() => {
    if (!restaurant) return ["Popular"];
    const cats = Array.from(new Set(restaurant.menu?.map((m) => m.category) || []));
    return ["Popular", ...cats];
  }, [restaurant]);

  const menu = useMemo(() => {
    if (!restaurant) return [];
    if (category === "Popular") return (restaurant.menu || []).slice(0, 6);
    return (restaurant.menu || []).filter((m) => m.category === category);
  }, [restaurant, category]);

  const toggleFav = async () => {
    try {
      const { data } = await api.post(`/favorites/${id}`);
      setFav(data.action === "added");
      toast.success(data.action === "added" ? "Added to favorites" : "Removed from favorites");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const scrollToCategory = (cat) => {
    setCategory(cat);
    const el = document.getElementById(`cat-section-${cat}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading || !restaurant) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="skeleton h-64 rounded-3xl" />
        <div className="skeleton h-8 w-2/3 mt-6" />
      </div>
    );
  }

  const cartFromThisRestaurant = cart.restaurant?.restaurant_id === id;
  const cartCount = cartFromThisRestaurant ? cart.items.reduce((s, i) => s + i.quantity, 0) : 0;
  const cartTotal = cartFromThisRestaurant ? cart.items.reduce((s, i) => s + i.price * i.quantity, 0) : 0;

  return (
    <div className="pb-40 max-w-6xl mx-auto">
      {/* Hero */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={restaurant.hero_url || restaurant.image_url} alt={restaurant.name}
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        <button data-testid="restaurant-back-btn" onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white text-eatup-charcoal shadow-md flex items-center justify-center">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button data-testid="restaurant-favorite-btn" onClick={toggleFav}
          className={`absolute top-4 right-4 w-10 h-10 rounded-full shadow-md flex items-center justify-center transition ${fav ? "bg-eatup-green text-white" : "bg-white text-eatup-charcoal"}`}>
          <Heart className={`w-5 h-5 ${fav ? "fill-white" : ""}`} />
        </button>
      </div>

      {/* Restaurant info block */}
      <div className="px-5 sm:px-8 -mt-6 relative z-10">
        <div className="bg-white rounded-3xl card-shadow p-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{restaurant.name}</h1>
          <div className="mt-2 text-sm text-neutral-500">{restaurant.categories?.join(" · ")}</div>
          <button onClick={() => setShowInfo(true)} className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="flex items-center gap-1 font-semibold">
              <Star className="w-4 h-4 fill-eatup-yellow text-eatup-yellow" />{restaurant.rating}
              <span className="text-neutral-500 font-normal">({restaurant.reviews})</span>
            </span>
            <span className="text-neutral-500">·</span>
            <span className="text-neutral-600">{restaurant.eta_minutes} min</span>
            <span className="text-neutral-500">·</span>
            <span className="text-neutral-600">${restaurant.delivery_fee?.toFixed(2)} delivery</span>
            <span className="text-eatup-green flex items-center gap-1 font-semibold ml-auto">
              More info <ChevronDown className="w-4 h-4" />
            </span>
          </button>
        </div>
      </div>

      {/* Closed badge + fulfillment toggle */}
      <div className="px-5 sm:px-8 mt-4 flex flex-wrap items-center gap-3">
        {!isOpen && (
          <div className="inline-flex items-center gap-2 rounded-full bg-red-500 text-white text-xs font-black uppercase tracking-widest px-4 py-2 shadow-md" data-testid="restaurant-closed-badge">
            Closed · Opens {restaurant.opens_at || "9:00"}
          </div>
        )}
        {isOpen && (
          <div className="inline-flex items-center gap-2 rounded-full bg-eatup-green/10 text-eatup-green border border-eatup-green/30 text-xs font-black uppercase tracking-widest px-4 py-2" data-testid="restaurant-open-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-eatup-green animate-pulse" />
            Open · Until {restaurant.closes_at || "10 PM"}
          </div>
        )}
        <div className="ml-auto rounded-full bg-white card-shadow p-1 flex" data-testid="menu-fulfillment-toggle">
          {["delivery", "pickup"].map((m) => (
            <button
              key={m}
              onClick={() => setFulfillment(m)}
              data-testid={`menu-fulfillment-${m}`}
              className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest transition ${
                fulfillment === m ? "bg-eatup-green text-white shadow-sm" : "text-neutral-500"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Sticky category nav (Uber-eats style) */}
      <div className="sticky top-[64px] z-30 bg-white/80 backdrop-blur border-b border-neutral-100 mt-6" ref={scrollRef}>
        <div className="px-5 sm:px-8 flex gap-1 overflow-x-auto no-scrollbar py-3">
          {menuCategories.map((c) => (
            <button
              key={c}
              data-testid={`menu-cat-${c.toLowerCase()}`}
              onClick={() => scrollToCategory(c)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition ${
                category === c ? "bg-eatup-charcoal text-white" : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Menu sections */}
      <div className="px-5 sm:px-8 mt-6 space-y-10">
        {menuCategories.map((c) => {
          const items = c === "Popular" ? (restaurant.menu || []).slice(0, 6) : (restaurant.menu || []).filter((m) => m.category === c);
          if (!items.length) return null;
          return (
            <section key={c} id={`cat-section-${c}`}>
              <h2 className="font-display text-2xl font-bold">{c}</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {items.map((m) => (
                  <button
                    key={m.item_id}
                    data-testid={`menu-item-${m.item_id}`}
                    onClick={() => isOpen ? setSelectedItem(m) : toast.error(`${restaurant.name} is closed — try again after ${restaurant.opens_at || "9:00"}.`)}
                    disabled={!isOpen}
                    className={`bg-white rounded-3xl card-shadow overflow-hidden flex items-stretch text-left transition ${isOpen ? "hover:card-shadow-hover" : "opacity-60 cursor-not-allowed"}`}
                  >
                    <div className="flex-1 p-5 min-w-0">
                      <div className="font-semibold leading-tight truncate">{m.name}</div>
                      <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{m.description}</p>
                      <div className="mt-3 flex items-center gap-2">
                        <span className="font-semibold">${m.price?.toFixed(2)}</span>
                        <span className="text-xs text-neutral-400">· {m.category}</span>
                      </div>
                    </div>
                    <div className="w-32 sm:w-36 relative shrink-0">
                      <img src={m.image_url} alt={m.name} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center">
                        <Plus className="w-4 h-4 text-eatup-charcoal" strokeWidth={2.5} />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Item modal */}
      {selectedItem && (
        <ItemModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onAdd={(qty, notes) => {
            addItem(restaurant, { ...selectedItem, notes }, qty);
            setSelectedItem(null);
            toast.success(`Added ${qty}× ${selectedItem.name}`);
          }}
        />
      )}

      {/* Info modal */}
      {showInfo && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setShowInfo(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 fade-up" onClick={(e) => e.stopPropagation()} data-testid="info-modal">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-2xl font-bold">{restaurant.name}</h3>
                <p className="text-sm text-neutral-500 mt-1">{restaurant.tagline}</p>
              </div>
              <button onClick={() => setShowInfo(false)} className="p-2 -mr-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <InfoRow icon={MapPin} label={restaurant.address} />
              <InfoRow icon={Clock} label={`${restaurant.eta_minutes} min delivery`} />
              <InfoRow icon={Bike} label={`$${restaurant.delivery_fee?.toFixed(2)} delivery fee`} />
              <InfoRow icon={Star} label={`${restaurant.rating} · ${restaurant.reviews} reviews`} />
            </div>
          </div>
        </div>
      )}

      {/* Sticky cart bar (uber eats style) */}
      {cartCount > 0 && (
        <div className="fixed bottom-24 md:bottom-8 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:max-w-md z-40">
          <button
            data-testid="view-cart-cta"
            onClick={() => navigate("/cart")}
            className="w-full bg-eatup-charcoal text-white rounded-full px-5 py-4 flex items-center justify-between font-semibold shadow-2xl hover:bg-black transition"
          >
            <span className="flex items-center gap-3">
              <span className="min-w-[26px] h-6 px-1.5 rounded-full bg-eatup-green text-white text-xs flex items-center justify-center font-bold">{cartCount}</span>
              View cart
            </span>
            <span className="tabular">${cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-eatup-green/10 text-eatup-green flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div>{label}</div>
    </div>
  );
}

function ItemModal({ item, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="item-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden fade-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative h-64 sm:h-72">
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center" data-testid="close-item-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <h3 className="font-display text-2xl font-bold">{item.name}</h3>
          <p className="mt-2 text-neutral-500">{item.description}</p>
          <div className="mt-3 font-display text-xl font-bold">${item.price?.toFixed(2)}</div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-widest font-bold text-neutral-500">Special instructions</label>
            <textarea
              data-testid="item-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. extra spicy, no onions"
              rows={2}
              className="mt-2 w-full p-3 rounded-2xl bg-eatup-gray border border-transparent focus:bg-white focus:border-eatup-green focus:ring-4 focus:ring-eatup-green/10 outline-none resize-none text-sm"
            />
          </div>

          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 bg-eatup-gray rounded-full px-1 py-1">
              <button data-testid="modal-decr" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                <Minus className="w-4 h-4" />
              </button>
              <span className="min-w-[24px] text-center font-bold tabular">{qty}</span>
              <button data-testid="modal-incr" onClick={() => setQty(qty + 1)} className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              data-testid="add-to-order-btn"
              onClick={() => onAdd(qty, notes)}
              className="flex-1 bg-eatup-charcoal hover:bg-black text-white rounded-full py-3 font-semibold flex items-center justify-between px-5"
            >
              <span>Add to order</span>
              <span className="tabular">${(item.price * qty).toFixed(2)}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
